import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Pencil, Download, Layers, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import DeckBundles from "@/components/DeckBundles";
import api, { API as API_BASE } from "@/lib/api";

const SlideStrip = ({ deck }) => (
  <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid={`series-slides-${deck.id}`}>
    {[1, 2, 3].map((page) => (
      <a key={page} href={`${API_BASE}/resources/${deck.id}/preview`} target="_blank" rel="noreferrer"
         className="block rounded-lg overflow-hidden border border-slate-200 bg-slate-100 hover:border-teal-400 hover:-translate-y-0.5 transition-all duration-300"
         data-testid={`series-slide-${deck.id}-${page}`}>
        <img src={`${API_BASE}/resources/${deck.id}/thumbnail?page=${page}`} loading="lazy"
             alt={`${deck.title} — sample slide ${page}`} className="w-full aspect-[16/9] object-cover object-top" />
      </a>
    ))}
  </div>
);

function DeckRow({ deck, index }) {
  const title = deck.title.replace(" — Workshop Slide Deck", "");
  return (
    <div className="grid lg:grid-cols-[1fr,1.15fr] gap-8 lg:gap-14 items-center py-12 border-t border-slate-200 first:border-0"
         data-testid={`series-deck-${deck.id}`}>
      <div className={index % 2 === 1 ? "lg:order-2" : ""}>
        <span className="font-display text-5xl font-bold text-slate-200 leading-none">{String(index + 1).padStart(2, "0")}</span>
        <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-3">{title}</h3>
        <p className="text-base text-slate-600 mt-3 leading-relaxed">{deck.description}</p>
        <p className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2 mt-4 inline-flex items-start gap-1.5">
          <Pencil className="w-3 h-3 mt-0.5 shrink-0" />
          Fully editable PowerPoint — adapt it for your own sessions
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <BuyButton itemId={deck.id} itemName={deck.title} price={deck.price}
                     className="bg-slate-900 hover:bg-teal-700 rounded-full" testId={`series-buy-${deck.id}`}>
            Add to basket · ${deck.price}
          </BuyButton>
          <Button asChild variant="outline" className="rounded-full" data-testid={`series-preview-${deck.id}`}>
            <a href={`${API_BASE}/resources/${deck.id}/preview`} target="_blank" rel="noreferrer">
              <Eye className="w-4 h-4" /> Preview sample pages
            </a>
          </Button>
        </div>
      </div>
      <div className={index % 2 === 1 ? "lg:order-1" : ""}><SlideStrip deck={deck} /></div>
    </div>
  );
}

export default function WorkshopSeries() {
  const [decks, setDecks] = useState([]);

  useEffect(() => {
    api.get("/resources")
      .then((r) => setDecks(r.data.filter((x) => x.category === "Workshop Decks")))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="workshop-series-page">
      <PageHero
        eyebrow="Professional learning series"
        title="Ten workshops. One complete library."
        subtitle="Every Teaching Pathways workshop, captured as a full slide deck you can read, present and adapt — built for pre-service teachers, mentors and schools."
      >
        <div className="flex flex-wrap gap-3" data-testid="series-hero-actions">
          <Button asChild className="rounded-full bg-teal-600 hover:bg-teal-700 h-12 px-7">
            <a href="#decks"><Layers className="w-4 h-4" /> Browse all 10 decks</a>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-12 px-7 bg-transparent text-white border-white/30 hover:bg-white hover:text-slate-900">
            <Link to="/resources">Resource store <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </PageHero>

      <Section className="!pb-0">
        <SectionHeading eyebrow="Bundles" title="Buy the set and save"
          subtitle="Single decks are $19 each. Take three of your choice for $49, or the whole series for $149." />
        <DeckBundles />
        <div className="flex flex-wrap gap-6 text-sm text-slate-600 border-t border-slate-200 pt-8">
          <span className="flex items-center gap-2"><Download className="w-4 h-4 text-teal-600" /> Instant download after payment</span>
          <span className="flex items-center gap-2"><Pencil className="w-4 h-4 text-teal-600" /> Editable PowerPoint (.pptx)</span>
          <span className="flex items-center gap-2"><Eye className="w-4 h-4 text-teal-600" /> Preview 3 slides of any deck</span>
        </div>
      </Section>

      <Section id="decks">
        <SectionHeading eyebrow="The full series" title="Every deck, slide by slide"
          subtitle="Scroll the whole series — first slides of each deck shown below. Click any slide to open the 3-slide preview." />
        {decks.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="series-empty">Decks are loading…</p>
        ) : (
          <div data-testid="series-deck-list">
            {decks.map((d, i) => <DeckRow key={d.id} deck={d} index={i} />)}
          </div>
        )}
      </Section>

      <div className="bg-slate-900 text-white">
        <Section className="!py-16 text-center">
          <Badge className="bg-teal-500/15 text-teal-300 hover:bg-teal-500/15 mb-4">Best value</Badge>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Take the complete library for $149</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">All ten decks, instantly downloadable and yours to keep — save $41 against buying them one by one.</p>
          <div className="mt-8 flex justify-center">
            <Button asChild className="rounded-full bg-teal-600 hover:bg-teal-700 h-12 px-8" data-testid="series-cta-bundles">
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>See the bundles</a>
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
